// ============================================
// FIXED ADMIN DASHBOARD (src/pages/admin-portal/AdminDashboard.jsx)
// ============================================
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Users, DollarSign, TrendingUp, Clock, Activity, AlertCircle } from 'lucide-react'
import { formatCurrency, formatRelativeTime } from '../../utils/formatters'
import { getDashboardStats, getPendingTransactions } from '../../services/adminService'
import { getAllActivities } from '../../services/activityService'

const AdminDashboard = () => {
  const navigate = useNavigate()
  const [stats, setStats] = useState(null)
  const [pending, setPending] = useState([])
  const [recentActivity, setRecentActivity] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadData()
    const interval = setInterval(loadData, 30000) // Refresh every 30s
    return () => clearInterval(interval)
  }, [])

  const loadData = async () => {
    setLoading(true)
    const [statsResult, pendingResult, activityResult] = await Promise.all([
      getDashboardStats(),
      getPendingTransactions(),
      getAllActivities({}, 10)
    ])

    if (statsResult.data) setStats(statsResult.data)
    if (pendingResult.data) setPending(pendingResult.data.slice(0, 5))
    if (activityResult.data) setRecentActivity(activityResult.data)
    setLoading(false)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    )
  }

  const statCards = [
    {
      title: 'Total Users',
      value: stats?.totalUsers || 0,
      icon: Users,
      color: 'blue',
      change: '+12%'
    },
    {
      title: 'Active Users',
      value: stats?.activeUsers || 0,
      icon: Activity,
      color: 'green',
      change: '+8%'
    },
    {
      title: 'Total Balance',
      value: formatCurrency(stats?.totalBalance || 0),
      icon: DollarSign,
      color: 'purple',
      change: '+24%'
    },
    {
      title: 'Pending Approvals',
      value: stats?.pendingTransactions || 0,
      icon: Clock,
      color: 'yellow',
      alert: (stats?.pendingTransactions || 0) > 0
    },
    {
      title: 'Total Transactions',
      value: stats?.totalTransactions || 0,
      icon: TrendingUp,
      color: 'indigo',
      change: '+15%'
    },
    {
      title: 'Approved Today',
      value: stats?.approvedTransactions || 0,
      icon: AlertCircle,
      color: 'green'
    }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
          Admin Dashboard
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Monitor and manage your banking system
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map((stat, index) => (
          <div key={index} className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  {stat.title}
                </p>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stat.value}
                </h3>
                {stat.change && (
                  <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                    {stat.change} from last month
                  </p>
                )}
                {stat.alert && (
                  <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">
                    Needs attention
                  </p>
                )}
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center bg-${stat.color}-100 dark:bg-${stat.color}-900/30`}>
                <stat.icon className={`w-6 h-6 text-${stat.color}-600 dark:text-${stat.color}-400`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pending Approvals Alert */}
      {(stats?.pendingTransactions || 0) > 0 && (
        <div className="alert alert-warning">
          <AlertCircle className="w-5 h-5" />
          <div className="flex-1">
            <p className="font-medium">
              You have {stats.pendingTransactions} pending transaction{stats.pendingTransactions !== 1 ? 's' : ''} awaiting approval
            </p>
            <button
              onClick={() => navigate('/admin-portal/pending-approvals')}
              className="text-sm underline mt-1 hover:no-underline"
            >
              Review now
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Pending Transactions */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              Recent Pending
            </h2>
            <button
              onClick={() => navigate('/admin-portal/pending-approvals')}
              className="text-primary-600 dark:text-primary-400 text-sm font-medium hover:underline"
            >
              View All
            </button>
          </div>

          {pending.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-gray-400 py-8">
              No pending transactions
            </p>
          ) : (
            <div className="space-y-3">
              {pending.map((transaction) => (
                <div
                  key={transaction.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-dark-700"
                >
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {transaction.users?.username}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {transaction.type} • {formatCurrency(transaction.amount)}
                    </p>
                  </div>
                  <span className="badge badge-warning">Pending</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              Recent Activity
            </h2>
            <button
              onClick={() => navigate('/admin-portal/activity')}
              className="text-primary-600 dark:text-primary-400 text-sm font-medium hover:underline"
            >
              View All
            </button>
          </div>

          {recentActivity.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-gray-400 py-8">
              No recent activity
            </p>
          ) : (
            <div className="space-y-3">
              {recentActivity.map((log) => (
                <div
                  key={log.id}
                  className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-dark-700"
                >
                  <Activity className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 dark:text-white truncate">
                      <span className="font-medium">{log.users?.username || 'System'}</span> {log.description || log.action}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatRelativeTime(log.created_at)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
          Quick Actions
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <button
            onClick={() => navigate('/admin-portal/pending-approvals')}
            className="btn btn-outline"
          >
            <Clock className="w-5 h-5" />
            Approvals
          </button>
          <button
            onClick={() => navigate('/admin-portal/users')}
            className="btn btn-outline"
          >
            <Users className="w-5 h-5" />
            Users
          </button>
          <button
            onClick={() => navigate('/admin-portal/transactions')}
            className="btn btn-outline"
          >
            <TrendingUp className="w-5 h-5" />
            Transactions
          </button>
          <button
            onClick={() => navigate('/admin-portal/analytics')}
            className="btn btn-outline"
          >
            <Activity className="w-5 h-5" />
            Analytics
          </button>
        </div>
      </div>
    </div>
  )
}

export default AdminDashboard